/* eslint-disable react-hooks/exhaustive-deps */
import React, { Suspense, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import customHistory from './customHistory';
import AppRoutes from './AppRoutes';
import { resetStore } from './actions/actions';
import useSyncRetailerInfo from './hooks/useSyncRetailerInfo';
import useSyncRetailerUserInfo from './hooks/useSyncRetailerUserInfo';
import useGlobalInboxMode from './hooks/useGlobalInboxMode';
import { loadConfig } from './config';
import MobileLimitedExperienceBanner from './components/shared/MobileLimitedExperienceBanner';
import FailedChargeAlertBannerMC from './presentation/components/FailedChargeAlertBannerMC';
import MiaFreeContactsExhaustedBannerMC from './presentation/components/MiaFreeContactsExhaustedBannerMC';
import { HelpCenterBarMC } from './presentation/components/HelpCenter';
import CallManager from './presentation/modules/calls/CallManager';
import { useWhatsappCallsUiEnabled } from './presentation/hooks/calls/useCallProvider';
// import { authService } from './services/authService';

const AUTH_ROUTE_PREFIXES = [
  '/login',
  '/register',
  '/sign_in',
  '/password',
  '/otp',
  '/invitation'
];

const isAuthRoute = (pathname) => AUTH_ROUTE_PREFIXES.some((route) => pathname.startsWith(route));

/**
 * Checks if the current URL matches the specified pattern to skip a process.
 * @returns {boolean} True if the process should be skipped, otherwise false.
 */
const shouldSkipProcessBasedOnUrl = () => {
  const { pathname } = window.location;
  const regex = /^\/retailers\/[^/]+\/customers\/[a-zA-Z0-9]+$/;
  return regex.test(pathname);
};

/**
 * App component that serves as the main entry point for the React application.
 * It manages the application state based on user session status and synchronizes
 * retailer and user information accordingly.
 *
 * @component
 *
 * @example
 * return (
 *   <App />
 * );
 */
const App = () => {
  const dispatch = useDispatch();
  const { retailer_info } = useSelector((reduxState) => reduxState.retailerUsersReducer);
  // Evita resetear repetidamente por el mismo slug ajeno (a prueba de bucles).
  const resetSlugRef = useRef(null);

  useEffect(() => {
    // Check if the logout flag is present in localStorage
    const userLoggedOut = localStorage.getItem('user_logged_out');
    const staleSlug = retailer_info?.slug && retailer_info.slug !== ENV.SLUG
      ? retailer_info.slug
      : null;

    if (userLoggedOut || (staleSlug && resetSlugRef.current !== staleSlug)) {
      resetSlugRef.current = staleSlug;

      const preserveKey = 'ws-widget-tutorial';
      const preserveValue = localStorage.getItem(preserveKey);

      // clear store
      localStorage.clear();
      sessionStorage.clear();

      if (preserveValue !== null) {
        localStorage.setItem(preserveKey, preserveValue);
      }
      // Clear all Redux state
      dispatch(resetStore());
    }

    // Inicializar el servicio de autenticación
    // authService.init();

    // Verificar si la sesión expiró en la última visita
    const sessionExpired = localStorage.getItem('session_expired');
    if (sessionExpired) {
      localStorage.removeItem('session_expired');
      dispatch(resetStore());
    }

    // return () => {
    //   authService.cleanup();
    // };
    // Re-evaluar cuando redux-persist rehidrata `retailer_info` (sesión previa).
  }, [dispatch, retailer_info?.slug]);

  const { fetchRetailerInfo } = useSyncRetailerInfo();
  const { fetchRetailerUserInfo, fetchRetailerUser } = useSyncRetailerUserInfo();

  // Initialize global inbox mode hook to manage state across the application
  useGlobalInboxMode();
  const whatsappCallsEnabled = useWhatsappCallsUiEnabled();

  const currentPath = window.location.pathname;
  const isOnboardingPage = /\/retailers\/[^/]+\/onboarding$/.test(currentPath);
  const showHelpCenter = !isOnboardingPage && !isAuthRoute(currentPath);
  const shouldMountCallManager = showHelpCenter && whatsappCallsEnabled;

  /**
   * useEffect hook to synchronize retailer and user information after
   * checking the logout status. These hooks will only execute if the user
   * is currently logged in (i.e., the logout flag is not present).
   */
  useEffect(() => {
    // No hacer llamadas API si estamos en la página de login o registro
    const authGuardPath = window.location.pathname;
    if (isAuthRoute(authGuardPath)) {
      return () => {};
    }

    loadConfig(true);
    fetchRetailerInfo();
    fetchRetailerUser();
    fetchRetailerUserInfo();

    const handleClick = (event) => {
      event.preventDefault();
      const href = event.currentTarget.getAttribute('href');

      const links = document.querySelectorAll('.merc-tooltip-active');
      links.forEach((link) => {
        link.classList.remove('merc-tooltip-active');
      });
      event.currentTarget.closest('.merc-tooltip').classList.add('merc-tooltip-active');

      customHistory.push(href);
    };

    const assignListeners = () => {
      const reactLinks = document.querySelectorAll('a[data-react-link="true"]');
      reactLinks.forEach((link) => {
        link.addEventListener('click', handleClick);
      });
    };

    const handleBeforeUnload = () => {
      dispatch({ type: 'RESET_TEMPORAL_STATE' });
    };

    if (!shouldSkipProcessBasedOnUrl()) {
      assignListeners();
    }
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      const reactLinks = document.querySelectorAll('a[data-react-link="true"]');
      reactLinks.forEach((link) => {
        link.removeEventListener('click', handleClick);
      });
    };
  }, []);

  return (
    <Suspense fallback={<div className="loading" />}>
      <MobileLimitedExperienceBanner />
      {showHelpCenter && <FailedChargeAlertBannerMC />}
      {showHelpCenter && <MiaFreeContactsExhaustedBannerMC />}
      <AppRoutes />
      {showHelpCenter && <HelpCenterBarMC />}
      {shouldMountCallManager && <CallManager />}
    </Suspense>
  );
};

export default App;
